import { LayoutTemplate, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { InfoTip, SETTINGS_HEADER_BUTTON_CLASS } from '@/modules/settings';
import { cn } from '@/lib/utils';
import type { RuleRecord } from '@/types/rules.types';
import { useRulesEnabled } from '../hooks/use-rules-enabled';
import { useTicketRules, useTicketRulesCatalog } from '../hooks/use-ticket-rules';
import { useCreateCustomPlaybook, useIsPlaybookAdmin } from '../hooks/use-create-custom-playbook';
import { shownOn, type PlaybookPlace } from '../lib/places';
import { RulesList } from './rules-list';

/**
 * Playbooks made from a setting that still has its own screen (Ingestion's blocked senders, teammate-owned
 * tickets, tag and intent filters; Intents' spam; the acknowledgement, hold message, reply delay, tags,
 * assignment, strip text; the Social Media Agent's table). That screen edits them, so the custom playbook
 * lists leave them out.
 */
const EDITED_ON_THEIR_SETTING = new Set([
	'inbound_filter',
	'human_assigned',
	'tag_gate',
	'intent_filter',
	'spam_intents',
	'spam_intents_answer',
	'auto_reply',
	'hold_message',
	'reply_delay',
	'preprocessing_tags',
	'assign_to_kim',
	'strip_patterns',
	'social_agent'
]);

const isCustom = (rule: RuleRecord): boolean => !(rule.origin === 'system' && rule.systemKey && EDITED_ON_THEIR_SETTING.has(rule.systemKey));

/** The page header's "Start from a template" and "Create custom playbook", for admins of a brand with playbooks on. */
export function CustomPlaybookButtons({ place }: { place: PlaybookPlace }) {
	const { available, create, openGallery, gallery } = useCreateCustomPlaybook(place);
	if (!available) return null;
	return (
		<>
			<button type='button' className={SETTINGS_HEADER_BUTTON_CLASS} onClick={openGallery}>
				<LayoutTemplate className='size-3.5' aria-hidden />
				Start from a template
			</button>
			<PillButton variant='primary' className='h-8 px-4' onClick={create}>
				<Plus className='size-3.5' aria-hidden />
				Create custom playbook
			</PillButton>
			{gallery}
		</>
	);
}

const DEFAULT_EMPTY_HINT = 'Use “Create custom playbook” at the top of the page to add one.';

/** This page's custom playbooks in the brand's run order, as rows in one card. */
export function CustomPlaybooksList({
	place,
	emptyHint = DEFAULT_EMPTY_HINT,
	narrow = false
}: {
	place: PlaybookPlace;
	emptyHint?: string;
	/** In a 600px settings section: no room for the weekly match count. */
	narrow?: boolean;
}) {
	const isAdmin = useIsPlaybookAdmin();
	const catalogQuery = useTicketRulesCatalog();
	const rulesQuery = useTicketRules();
	const catalog = catalogQuery.data;
	const all = rulesQuery.data ?? [];
	const rules = all.filter((rule) => isCustom(rule) && shownOn(rule, place));

	return (
		<div id='custom-playbooks' className='w-full'>
			{rulesQuery.isError || catalogQuery.isError ? (
				<div className='flex items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600'>
					Your custom playbooks could not be loaded.
					<Button variant='outline' size='sm' onClick={() => void Promise.all([rulesQuery.refetch(), catalogQuery.refetch()])}>
						Retry
					</Button>
				</div>
			) : rulesQuery.isLoading || !catalog ? (
				<Skeleton className='h-16 w-full rounded-2xl' />
			) : rules.length === 0 ? (
				<p className='rounded-2xl border border-gray-500 px-4 py-5 text-center text-[13px] text-gray-800'>
					No custom playbooks yet.{isAdmin ? ` ${emptyHint}` : ''}
				</p>
			) : (
				<RulesList rules={rules} allRules={all} catalog={catalog} isAdmin={isAdmin} compact hideMatches={narrow} />
			)}
		</div>
	);
}

interface CustomPlaybooksProps {
	place: PlaybookPlace;
	/** What these run on, in the page's own words, behind the (i) by the label. */
	description: string;
	className?: string;
}

/** A settings page's "Custom playbooks" section, styled like its other sections. Only for a brand with playbooks on. */
export function CustomPlaybooks({ place, description, className }: CustomPlaybooksProps) {
	if (!useRulesEnabled()) return null;
	return (
		<section className={cn('flex w-[600px] max-w-full flex-col gap-3', className)}>
			<div className='flex items-center gap-1'>
				<p className='text-[12px] font-medium leading-4 text-gray-700'>Custom playbooks</p>
				<InfoTip label='About custom playbooks'>{description}</InfoTip>
			</div>
			<CustomPlaybooksList place={place} narrow />
		</section>
	);
}
